import React, { useRef } from 'react'
import { Dimensions, FlatList, View } from 'react-native'
import { ModalProps } from 'react-native-modal'

import { GameStatsModelType } from '../../models/api-store'
import { fontFamily } from '../../theme/fontFamily'
import { Bot } from '../Bot/Bot'
import { CommonModal } from '../CommonModal/commonModal'
import { Text } from '../text/text'
import styles from './styles'

const initialPlayerData = {
  playerId: '',
  playerName: 'Player',
  isLeft: false,
  chips: 0,
  stack: 0,
  isRemovedByTeacher: false
}

export interface LeaderboardPlayer {
  playerId: string
  playerName: string
  isLeft: boolean
  chips: number
  stack: number
  isRemovedByTeacher: boolean
}

export interface LeaderBoardProps extends Partial<ModalProps> {
  visible: boolean
  message: string
  observers: number[]
  onAction: () => void
  onClose: () => void
  boardData?: GameStatsModelType
  myPlayerId: number
  isAccessedFromDrawer: boolean
  isGameOver?: boolean
  isDev: boolean
  totalChipsOnTable: number
}

const DeviceHeight = Dimensions.get('screen').height - 260

export const LeaderBoard = (props: LeaderBoardProps) => {
  const {
    visible,
    onAction,
    onClose,
    message,
    observers,
    boardData,
    myPlayerId,
    isAccessedFromDrawer,
    isGameOver,
    isDev,
    totalChipsOnTable,
    ...rest
  } = props
  // const [isGameRunning, setIsGameRunning] = React.useState(false)
  const [runTime, setRunTime] = React.useState('00:00:00')
  const [entries, setEntries] = React.useState('--')
  const [myRanking, setMyRanking] = React.useState<string | undefined>()
  const [playerData, setPlayerData] = React.useState<LeaderboardPlayer[]>([initialPlayerData])
  const [winnerData, setWinnerData] = React.useState([initialPlayerData])
  const [leaderboardWinMessage, setLeaderboardWinMessage] = React.useState('')
  const totalChipsCount = useRef<number>(0)

  React.useEffect(() => {
    if (visible === false) {
      setRunTime('00:00:00')
      setEntries('--')
      setMyRanking('--')
      setPlayerData([initialPlayerData])
      setWinnerData([initialPlayerData])
      setLeaderboardWinMessage('')
    }
  }, [visible])

  React.useEffect(() => {
    if (boardData) {
      const data = boardData
      setRunTime(data.runTime ?? '00:00:00')
      //@ts-ignore
      setPlayerData(data.players)
      setWinnerData(data.winners)
      // setIsGameRunning(data.isGameRunning)
    }
  }, [boardData, boardData?.runTime])

  React.useEffect(() => {
    if (playerData.length > 0) {
      totalChipsCount.current = 0
      const playerDataIndex = playerData.findIndex((data) => Number(data.playerId) === myPlayerId)
      if (playerDataIndex < 0) {
        setMyRanking('--')
      } else {
        const myRank = winnerData.length > 0 ? playerDataIndex + 1 : 1
        setMyRanking(myRank.toString())
      }
      const leaderBoardPlayerData = playerData.length > 9 ? playerData.slice(0, 9) : playerData
      leaderBoardPlayerData.forEach((element) => {
        totalChipsCount.current = totalChipsCount.current + element.chips
      })
      setEntries(playerData.length.toString())

      const leaderBoardMessage =
        winnerData.length > 0
          ? Number(winnerData[0].playerId) === myPlayerId
            ? 'You Won'
            : `${winnerData[0].playerName} Won`
          : ''
      setLeaderboardWinMessage(leaderBoardMessage)
    }
  }, [myPlayerId, playerData, winnerData, winnerData.length, leaderboardWinMessage])

  const isBotWinningMessage = leaderboardWinMessage.includes('\u{1F916}') ?? false
  const parsedWinningText = isBotWinningMessage
    ? leaderboardWinMessage.split('\u{1F916}').join('')
    : leaderboardWinMessage

  // const onPress = debounce(() => {
  //   onAction()
  //   onClose()
  // }, 250)

  const TotalChips = () => {
    return (
      <View style={styles.totalChipsContainer}>
        <View style={styles.totalChipsView}>
          <Text tx="leaderboard.totalChips" style={styles.totalChipsTitle} />
          <Text
            tx="leaderboard.totalChipsValue"
            txOptions={{ totalChipsValue: totalChipsCount.current }}
            style={styles.totalChipsAmount}
          />
        </View>
        <View style={[styles.totalChipsView, { marginTop: 4 }]}>
          <Text tx="leaderboard.totalChipsFromTable" style={styles.totalChipsTitle} />
          <Text
            tx="leaderboard.totalChipsFromTableValue"
            txOptions={{ totalChipsFromTableValue: totalChipsOnTable }}
            style={styles.totalChipsAmount}
          />
        </View>
      </View>
    )
  }

  const modalContent = () => {
    return (
      <View style={{ backgroundColor: '#0D2063' }}>
        {(!isAccessedFromDrawer && isGameOver) || (isAccessedFromDrawer && isGameOver) ? (
          <View style={styles.winningHeadingContainer}>
            {isBotWinningMessage && <Bot width={32} height={32} />}
            <Text style={styles.winningHeading} tx={'common.label'} txOptions={{ label: parsedWinningText }} />
          </View>
        ) : null}
        {(!isAccessedFromDrawer && isGameOver) || (isAccessedFromDrawer && isGameOver) ? (
          <View style={styles.separator} />
        ) : null}
        <Text
          style={[styles.leaderBoardText, isAccessedFromDrawer && !isGameOver ? { paddingTop: 20 } : {}]}
          tx={'leaderboard.leaderboardHeading'}
        />
        <View style={{ flexDirection: 'row', marginTop: 12 }}>
          <View style={styles.myDataContainer}>
            <Text tx="leaderboard.myRank" style={styles.myDataLabels} />
            <Text tx="leaderboard.myRankValue" txOptions={{ rank: myRanking }} style={styles.myDataValues} />
          </View>
          <View style={styles.myDataContainer}>
            <Text tx="leaderboard.entries" style={styles.myDataLabels} />
            <Text
              tx="leaderboard.entriesValue"
              txOptions={{ entries: entries !== '--' ? Math.min(9, Number(entries)) : entries }}
              style={styles.myDataValues}
            />
          </View>
          <View style={styles.myDataContainer}>
            <Text tx="leaderboard.runtime" style={styles.myDataLabels} />
            <Text tx="leaderboard.runtimeValue" txOptions={{ runtime: runTime }} style={styles.myDataValues} />
          </View>
        </View>
        <View style={styles.tableHeader}>
          <View style={styles.rankHeaderContainer}>
            <Text tx="leaderboard.rank" style={styles.rankHeading} />
          </View>
          <View style={styles.stackHeaderContainer}>
            <Text tx="leaderboard.stack" style={styles.stackHeading} />
          </View>
          <View style={styles.nameHeaderContainer}>
            <Text tx="leaderboard.name" style={styles.nameHeading} />
          </View>
          {/* <View style={styles.raHeaderContainer}>
            <Text tx="leaderboard.ra" style={styles.raHeading} />
          </View> */}
        </View>
        <View style={{ maxHeight: DeviceHeight }}>
          {/**
           * TODO: Highlight my rank with the real data
           */}
          <FlatList
            data={playerData.length > 9 ? playerData.slice(0, 9) : playerData}
            extraData={[playerData, boardData]}
            keyExtractor={(item) => item.playerId}
            renderItem={({ item, index }) => {
              const playerName = item?.playerName
              const isBotPlayerName = playerName.includes('\u{1F916}') ?? false

              if (!String(observers).includes(item.playerId)) {
                return (
                  <View
                    style={[
                      { flexDirection: 'row', paddingVertical: 12, paddingLeft: 16 },
                      { backgroundColor: Number(item.playerId) === myPlayerId ? '#ffffff10' : 'transparent' }
                    ]}
                  >
                    <View style={{ alignItems: 'center', width: '15%' }}>
                      <Text
                        style={{
                          fontSize: 12,
                          fontFamily: fontFamily.sfProDisplayBold,
                          color: Number(item.playerId) === myPlayerId ? '#FFBB0E' : 'white'
                        }}
                      >
                        {winnerData.length > 0 ? index + 1 : 1}
                      </Text>
                    </View>
                    <View style={{ width: '25%' }}>
                      <Text style={styles.stackValue}>
                        {/* {item.chips >= 1000 ? `${(item.chips / 1000).toFixed(1)}k` : item.chips} */}
                        {item.chips}
                      </Text>
                    </View>
                    <View style={{ paddingLeft: 20, width: '60%', paddingRight: 3 }}>
                      <Text
                        {...props}
                        numberOfLines={1}
                        ellipsizeMode={'tail'}
                        style={[
                          styles.nameValue,
                          {
                            textDecorationLine: item?.isLeft ? 'line-through' : 'none'
                          }
                        ]}
                      >
                        {isBotPlayerName && <Bot width={20} height={20} />}
                        {isBotPlayerName ? playerName.split('\u{1F916}').join('') : playerName}
                        {item?.isLeft && !item?.isRemovedByTeacher ? ' (left)' : ''}
                        {item?.isRemovedByTeacher ? ' (Removed)' : ''}
                      </Text>
                    </View>
                    {/**
                     * R+A coloumn is for multitable
                     */}
                    {/* <View style={styles.raValueContainer}>
                      <Text style={styles.raValue}>{item.ra}</Text>
                    </View> */}
                  </View>
                )
              }
              return null
            }}
            ListFooterComponent={isDev ? <TotalChips /> : null}
            style={{ paddingVertical: isDev ? 10 : 20, maxHeight: DeviceHeight }}
          />
        </View>
      </View>
    )
  }

  return (
    <CommonModal
      {...rest}
      visible={visible}
      onClose={onClose}
      showCloseButton={true}
      containerStyle={{ borderRadius: 6 }}
      isGameOver={isGameOver}
    >
      {modalContent()}
    </CommonModal>
  )
}
